import isSimOnlyPlan from './is-sim-only-plan';
import CONSTANTS from '../constants/common';
import { composeRequestParams } from './common';
import { INFO_TYPES } from '../components/Info';
import { navigation } from '../middlewares/navigation-constants';
import { Plan, Subscription } from '../types/plan.types';
import { PLAN, PLAN_GROUPS } from '../constants/plan';
import notifications from '../config/notifications';
import { KeyValue } from '../types/common.types';

export const getMobileShareLinesOfMainLine = (user, service?): string => {
  if (!service) {
    return '';
  }
  const mainlines = user?.mainLines || {};
  const selectedMainline = mainlines[service?.serviceId];
  if (selectedMainline) {
    const msLines = selectedMainline.implGroupMember;
    if (msLines?.length) {
      return msLines
        .map(msLine => {
          return msLine.msisdn;
        })
        .join(', ');
    }
  }
  return '';
};

export const isMainLineHasMobileShare = (
  user,
  service,
  isEshopLineSelection = false
): boolean => {
  const mainlines = user?.mainLines || {};
  const selectedMainline = mainlines[service?.serviceId];
  // eshop line selection will include mainlines as part of the group member
  return (
    selectedMainline?.implGroupMember?.length > (isEshopLineSelection ? 1 : 0)
  );
};

export const isMSMainLine = (service): boolean => {
  if (!service) return false;
  return CONSTANTS.MS_MAINLINE_PLANS.some(plan => {
    return service.offeringName
      ?.toUpperCase()
      ?.replace(/ /g, '')
      .includes(plan);
  });
};

export const shouldShowMSConversionPopup = (
  selectedPlan,
  user,
  service,
  abTestingLineSelectionEnabled
): boolean => {
  return (
    isSimOnlyPlan(selectedPlan) &&
    isMSMainLine(service) &&
    isMainLineHasMobileShare(user, service, abTestingLineSelectionEnabled)
  );
};

export const getPendingOrderParams = (isOnePass: boolean) => {
  return composeRequestParams({
    [`${CONSTANTS.URL_PARAMS.INFO_TITLE}`]: 'PENDING_ORDER_TITLE',
    [`${CONSTANTS.URL_PARAMS.INFO_MESSAGE}`]: isOnePass
      ? 'PENDING_ORDER_MESSAGE'
      : 'PENDING_ORDER_MESSAGE_WORRY_FREE',
    [`${CONSTANTS.URL_PARAMS.INFO_CTA_TEXT}`]: isOnePass
      ? 'PENDING_ORDER_CTA_TEXT'
      : null,
    [`${CONSTANTS.URL_PARAMS.INFO_TYPE}`]: INFO_TYPES.ERROR,
    [`${CONSTANTS.URL_PARAMS.INFO_CALLBACK_URL}`]: isOnePass
      ? navigation.CHOOSE_NUMBER_PAGE
      : null,
    [`${CONSTANTS.URL_PARAMS.CHAT_WITH_US}`]: isOnePass
      ? 'CHAT_WITH_US_ONE_PASS'
      : 'CHAT_WITH_US_ONE_WORRY_FREE'
  });
};

export const getEligibleStatus = ({
  service,
  eshopSubscriptionEnabled,
  selectedPlan,
  plans,
  isMSNew
}: {
  service: Subscription;
  eshopSubscriptionEnabled?: boolean;
  selectedPlan: Plan;
  plans: Plan[];
  isMSNew: boolean;
}): { message: string; status: boolean; ciNotEligible?: boolean } => {
  const defaultValue = { message: '', status: true, ciNotEligible: false };
  const needFeCheck =
    service.recontractEligibility?.ineligibleCode === PLAN.ERROR_CODE.FE_CHECK;
  switch (true) {
    case !eshopSubscriptionEnabled: {
      return defaultValue;
    }

    case !isMSNew &&
      service.recontractEligibility?.ineligibleCode ===
        PLAN.ERROR_CODE.CI_NOT_ELIGIBLE: {
      return {
        message: notifications.ELIGIBILITY_NOT_CI_ELIGIBLE.text,
        status: false,
        ciNotEligible: true
      };
    }

    // Mobile share can recon to non mobile share even it has done 12 months contract yet
    case needFeCheck &&
      serviceIsMobileShare(plans, service) &&
      isMobileShare(selectedPlan): {
      return {
        message: notifications.MS_RECONTRACT_NO_SERVICES_FOUND.text,
        status: false
      };
    }

    // Simonly lite can recon to mobile plan even it has done 12 months contract yet
    case needFeCheck &&
      serviceIsSimOnly(plans, service) &&
      (isSimOnlyPlan(selectedPlan) || isMobileShare(selectedPlan)): {
      return {
        message: notifications.MS_RECONTRACT_NO_SERVICES_FOUND.text,
        status: false
      };
    }
    default: {
      return defaultValue;
    }
  }
};

export const getMSLinesForRecon = ({
  services
}: {
  services: Subscription[];
  eshopSubscriptionEnabled: boolean;
}): Subscription[] => {
  return services.filter(service =>
    service?.offeringName
      .toUpperCase()
      .replace(/ /g, '')
      .includes(PLAN.MS_OFFERING_NAME)
  );
};

export const getMainLinesForMSNew = ({
  services = [],
  plans
}: {
  services: Subscription[];
  plans: Plan[];
}): Subscription[] => {
  if (!services.length) return [];
  const hasMainlineGroup = plan => {
    return plan?.mainlineGroup?.toUpperCase() === PLAN.MS_OFFERING_NAME;
  };
  return services.filter(service => {
    const plan = plans.find(p => p.planId === service.offeringId);
    return (
      !isMobileShare(plan) &&
      plans.some(
        plan => plan.planId === service.offeringId && hasMainlineGroup(plan)
      )
    );
  });
};

export const isMobileShare = (plan: Plan) => {
  return !!plan?.groupName?.toUpperCase()?.includes(PLAN_GROUPS.MOBILE_SHARE);
};

export const assignMaxChildError = (
  service: Subscription,
  mainLines: KeyValue
) => {
  const mainLine = mainLines[service.serviceId];
  const hasChildren = mainLine && mainLine.implGroupMember;
  if (hasChildren && mainLine.implGroupMember.length >= 3) {
    return {
      ...service,
      disabled: true,
      errorMsg: 'ELIGIBILITY_MOBILE_SHARE_MAX_3'
    };
  }
  return service;
};

export const serviceIsSimOnly = (plans: Plan[], subscription: Subscription) => {
  const plan = plans.find(plan => plan.planId === subscription.offeringId);
  return isSimOnlyPlan(plan);
};

export const serviceIsMobileShare = (
  plans: Plan[],
  subscription: Subscription
) => {
  const plan = plans.find(plan => plan.planId === subscription.offeringId);
  return isMobileShare(plan);
};

export const filterMainline = (plans: Plan[]) => (
  subscription: Subscription
) => {
  return !serviceIsMobileShare(plans, subscription);
};
